import {
  queryOptions as queryOptionsBuilder,
  useQuery,
  useSuspenseQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult
} from '@tanstack/react-query';

import type {
  AppVersionGetVersionPolicyParams,
  AppVersionPolicyResponse
} from '../model';

import { customInstance } from '../../mutator/custom-instance-new.ts';
import type { ErrorType } from '../../mutator/custom-instance-new.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Public: the app checks it before sign-in. It stays reachable from builds below the minimum, since those need it to show the update screen.
 * @summary Get the native app version policy
 */
export const appVersionGetVersionPolicy = (
    params: AppVersionGetVersionPolicyParams,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AppVersionPolicyResponse>(
      {url: `/app/version-policy`, method: 'GET',
        params, signal
    },
      options);
    }




export const getAppVersionGetVersionPolicyQueryKey = (params?: AppVersionGetVersionPolicyParams,) => {
    return [
    `/app/version-policy`, ...(params ? [params] : [])
    ] as const;
    }


export const getAppVersionGetVersionPolicyQueryOptions = <TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(params: AppVersionGetVersionPolicyParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAppVersionGetVersionPolicyQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>> = ({ signal }) => appVersionGetVersionPolicy(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AppVersionGetVersionPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>>
export type AppVersionGetVersionPolicyQueryError = ErrorType<unknown>


export function useAppVersionGetVersionPolicy<TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(
 params: AppVersionGetVersionPolicyParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof appVersionGetVersionPolicy>>,
          TError,
          Awaited<ReturnType<typeof appVersionGetVersionPolicy>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAppVersionGetVersionPolicy<TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(
 params: AppVersionGetVersionPolicyParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof appVersionGetVersionPolicy>>,
          TError,
          Awaited<ReturnType<typeof appVersionGetVersionPolicy>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAppVersionGetVersionPolicy<TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(
 params: AppVersionGetVersionPolicyParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get the native app version policy
 */

export function useAppVersionGetVersionPolicy<TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(
 params: AppVersionGetVersionPolicyParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAppVersionGetVersionPolicyQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAppVersionGetVersionPolicySuspenseQueryOptions = <TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(params: AppVersionGetVersionPolicyParams, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAppVersionGetVersionPolicyQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>> = ({ signal }) => appVersionGetVersionPolicy(params, requestOptions, signal);





   return  queryOptionsBuilder({ queryKey, queryFn, ...queryOptions}) as UseSuspenseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> } & { throwOnError?: ((this: never, error: TError) => boolean) & { readonly __inferenceOnly: never } }
}

export type AppVersionGetVersionPolicySuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>>
export type AppVersionGetVersionPolicySuspenseQueryError = ErrorType<unknown>


export function useAppVersionGetVersionPolicySuspense<TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(
 params: AppVersionGetVersionPolicyParams, options: { query:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAppVersionGetVersionPolicySuspense<TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(
 params: AppVersionGetVersionPolicyParams, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAppVersionGetVersionPolicySuspense<TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(
 params: AppVersionGetVersionPolicyParams, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get the native app version policy
 */

export function useAppVersionGetVersionPolicySuspense<TData = Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError = ErrorType<unknown>>(
 params: AppVersionGetVersionPolicyParams, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof appVersionGetVersionPolicy>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAppVersionGetVersionPolicySuspenseQueryOptions(params,options)

  const query = useSuspenseQuery(queryOptions, queryClient) as  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






